import type { OrganizationDefinition, OrganizationNode } from '@shared/types';
import {
  AlertTriangle,
  Building2,
  Calendar,
  Copy,
  Edit2,
  History,
  MoreHorizontal,
  Plus,
  Trash2,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { cn } from '@/lib/utils';

const API_BASE = apiUrl('/api');

// Fetch organization data from API
const fetchOrganizations = async (
  organizationId: string,
  academicYear: number,
  showInactive: boolean,
): Promise<OrganizationNode[]> => {
  const response = await fetch(
    `${API_BASE}/organizations?organizationId=${organizationId}&academicYear=${academicYear}&showInactive=${showInactive}`,
  );
  if (!response.ok) {
    throw new Error(`API Error: ${response.status}`);
  }
  return await response.json();
};

// Helper: Flatten Tree into a List
interface FlatOrgNode {
  node: OrganizationNode;
  def: OrganizationDefinition;
  depth: number;
}

const flattenTree = (nodes: OrganizationNode[], depth = 0): FlatOrgNode[] => {
  const result: FlatOrgNode[] = [];

  const traverse = (currentNodes: OrganizationNode[], currentDepth: number) => {
    for (const node of currentNodes) {
      if (!node.currentDefinition) continue;

      result.push({ node, def: node.currentDefinition, depth: currentDepth });

      if (node.children && Array.isArray(node.children) && node.children.length > 0) {
        traverse(node.children, currentDepth + 1);
      }
    }
  };

  traverse(nodes, depth);
  return result;
};

/**
 * Get year tab label based on current academic year
 */
const getYearTabLabel = (year: number, currentAy: number, t: (key: string) => string): string => {
  if (year < currentAy) return t('org.tabs.past');
  if (year === currentAy) return t('org.tabs.current');
  if (year === currentAy + 1) return t('org.tabs.planning');
  return t('org.tabs.future');
};

/**
 * Get organization type icon text
 */
const getOrgTypeIcon = (type: string): string => {
  switch (type) {
    case 'UNIVERSITY':
      return '大';
    case 'CAMPUS':
      return 'C';
    case 'FACULTY':
      return '学';
    default:
      return '部';
  }
};

/**
 * Get organization type color class
 */
const getOrgTypeColor = (type: string): string => {
  switch (type) {
    case 'UNIVERSITY':
      return 'bg-blue-600';
    case 'CAMPUS':
      return 'bg-green-600';
    case 'FACULTY':
      return 'bg-purple-600';
    default:
      return 'bg-orange-600';
  }
};

export default function OrganizationList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();

  // State
  const currentAy = 2025;
  const [academicYear, setAcademicYear] = useState(currentAy);
  const [_isCorrectionMode, _setIsCorrectionMode] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationNode[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine effective tenant ID based on user role
  const effectiveTenantId = useMemo(() => {
    if (!user) return null;
    if (['system_admin', 'system_operator', 'system_support'].includes(user.role)) {
      return selectedTenant?.id || null;
    }
    return user.tenantId;
  }, [user, selectedTenant]);

  // Fetch organizations
  useEffect(() => {
    if (!effectiveTenantId) {
      setOrganizations([]);
      return;
    }
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await fetchOrganizations(effectiveTenantId, academicYear, showInactive);
        if (Array.isArray(data)) {
          setOrganizations(data);
        } else {
          // Fallback or error if not array
          throw new Error('Invalid API response');
        }
      } catch (_err) {
        setError(t('common.dataLoadFailed', 'データの取得に失敗しました。'));
        setOrganizations([]);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [effectiveTenantId, academicYear, showInactive, t]);

  // Flatten for table rendering
  const flatOrgs = useMemo(() => flattenTree(organizations), [organizations]);

  // No tenant selected view
  if (!effectiveTenantId) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center border-2 border-dashed border-gray-200 rounded-xl bg-gray-50 m-6">
        <Building2 className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('sidebar.noPartnersAvailable')}</h3>
        <p className="text-gray-500 max-w-sm mb-6">{t('menu.tenants')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('org.title')}</h1>
          <p className="text-gray-500 mt-1">{t('org.description')}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="gap-2" asChild>
            <Link to="/site/change-requests">
              <History className="w-4 h-4" />
              {t('org.changeRequests')}
            </Link>
          </Button>
          <Button className="gap-2 bg-blue-600 hover:bg-blue-700 shadow-md" asChild>
            <Link to="/site/organizations/new">
              <Plus className="w-4 h-4" />
              {t('org.add')}
            </Link>
          </Button>
        </div>
      </div>

      <Tabs defaultValue={academicYear.toString()} onValueChange={(v) => setAcademicYear(Number.parseInt(v, 10))}>
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            {[currentAy - 1, currentAy, currentAy + 1].map((year) => (
              <TabsTrigger key={year} value={year.toString()} className="gap-2">
                <Calendar className="w-4 h-4" />
                {year} <span className="text-xs text-gray-500">({getYearTabLabel(year, currentAy, t)})</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="flex items-center gap-4 bg-white p-2 rounded-lg border shadow-sm">
            <div className="flex items-center gap-2">
              <Switch id="show-inactive" checked={showInactive} onCheckedChange={setShowInactive} />
              <label htmlFor="show-inactive" className="text-sm cursor-pointer select-none">
                {t('org.showHistory')}
              </label>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border p-0 overflow-hidden min-h-[500px]">
          {(() => {
            if (isLoading) {
              return (
                <div className="flex items-center justify-center h-[500px]">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">{t('common.loading')}</p>
                  </div>
                </div>
              );
            }

            if (error) {
              return (
                <div className="flex flex-col items-center justify-center h-[500px] text-red-500">
                  <AlertTriangle className="w-16 h-16 mb-4 text-red-200" />
                  <h3 className="text-lg font-semibold text-red-700 mb-2">{t('common.error')}</h3>
                  <p className="text-sm text-center max-w-md">{error}</p>
                  <Button onClick={() => globalThis.location.reload()} variant="outline" className="mt-4">
                    {t('common.retry')}
                  </Button>
                </div>
              );
            }

            if (organizations.length === 0) {
              return (
                <div className="flex flex-col items-center justify-center h-[500px] text-gray-500">
                  <Building2 className="w-16 h-16 mb-4 text-gray-300" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">{t('org.noOrgs.title')}</h3>
                  <p className="text-sm text-center max-w-md">{t('org.noOrgs.description')}</p>
                </div>
              );
            }

            return (
              <Table>
                <TableHeader className="bg-gray-50/50">
                  <TableRow>
                    <TableHead className="w-[350px] pl-6">{t('organizations.name')}</TableHead>
                    <TableHead className="w-[120px]">{t('organizations.code')}</TableHead>
                    <TableHead className="w-[100px]">{t('organizations.type')}</TableHead>
                    <TableHead className="w-[200px]">{t('organizations.validFrom')}</TableHead>
                    <TableHead className="w-[100px]">{t('organizations.status')}</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* Info Header Row */}
                  <TableRow className="bg-blue-50/20 hover:bg-blue-50/20">
                    <TableCell colSpan={6} className="text-sm text-gray-500 py-2 pl-6">
                      {t('org.structureInfo', {
                        year: academicYear,
                        start: `${academicYear}-04-01`,
                        end: `${academicYear + 1}-03-31`,
                      })}
                      <span className="ml-4 bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">
                        {t('org.unitCount', { count: flatOrgs.length })}
                      </span>
                    </TableCell>
                  </TableRow>

                  {flatOrgs.map(({ node, def, depth }) => {
                    const isInactive = def.status === 'ABOLISHED' || def.status === 'STOPPED_RECRUITMENT';
                    const isNew = def.validFrom === `${academicYear}-04-01`;

                    return (
                      <TableRow
                        key={node.id}
                        className={cn(
                          'group hover:bg-blue-50/30 transition-colors',
                          isInactive && 'opacity-60 bg-gray-50',
                        )}
                      >
                        <TableCell className="py-3">
                          <div className="flex items-center gap-3" style={{ paddingLeft: `${depth * 24}px` }}>
                            <div
                              className={cn(
                                'flex-shrink-0 w-6 h-6 rounded flex items-center justify-center text-white font-bold text-[10px]',
                                getOrgTypeColor(node.type),
                              )}
                            >
                              {getOrgTypeIcon(node.type)}
                            </div>
                            <span className={cn('font-medium text-gray-900', depth === 0 && 'font-bold text-base')}>
                              {def.nameFormal}
                            </span>
                            {isNew && (
                              <Badge className="bg-green-100 text-green-700 border-green-200 text-[10px] px-1.5 py-0 h-5">
                                NEW
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-gray-600 text-sm">{def.code}</TableCell>
                        <TableCell>
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                            {t(`organizations.types.${node.type}`, node.type)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {/* Enhanced History View: Valid From ~ Valid To */}
                          <div className="flex flex-col text-xs text-gray-600">
                            <div className="flex items-center gap-1 font-mono">
                              {def.validFrom ? def.validFrom.replaceAll('-', '/') : '...'}
                              <span className="text-gray-400">~</span>
                              {def.validTo ? def.validTo.replaceAll('-', '/') : '...'}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {isInactive ? (
                            <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-200">
                              廃止
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              有効
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuLabel>{t('common.actions')}</DropdownMenuLabel>
                              <DropdownMenuSeparator />

                              <DropdownMenuItem>
                                <Copy className="w-4 h-4 mr-2" />
                                {t('org.actions.history')}
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-xs text-gray-400 font-normal">
                                改廃アクション
                              </DropdownMenuLabel>

                              <DropdownMenuItem>
                                <Edit2 className="w-4 h-4 mr-2" />
                                {t('org.actions.rename')}
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Trash2 className="w-4 h-4 mr-2" />
                                {t('org.actions.abolish')}
                              </DropdownMenuItem>
                              <DropdownMenuItem>
                                <Users className="w-4 h-4 mr-2" />
                                {t('org.actions.integrate')}
                              </DropdownMenuItem>

                              <DropdownMenuSeparator />
                              <DropdownMenuItem className="text-red-600">
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                {t('org.actions.correct')}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            );
          })()}
        </div>
      </Tabs>
    </div>
  );
}
